// Common //
import { Dispatch, ReactNode, RefObject, SetStateAction } from 'react'
import Handsontable from 'handsontable'
import { Figure } from 'react-plotly.js'
import { IconDefinition, IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core'
import { ClassNameMap, CSSProperties } from '@mui/styles'
import { AutocompleteClassKey } from '@mui/material'
import {
  ChoiceOptionsAndIndexInfo,
  ChoiceParamsInfo,
  GetFormColumnsData,
  IndexFilterChoices,
  IndexTextFilterChoices,
  SelectorOptionsAndIndexInfo,
  SelectorParamsInfo,
  SelectorResultType,
  SetSelectorValueParams
} from '../../../common/types/model'
import { Context, InterfaceEvent, InterfaceEventType } from '../containers/types'
import { InterfaceIndexValuesData } from 'common/types/interface'
import { TaskColumnFilters } from 'common/types/task'
import { Settings } from 'handsontable/plugins/contextMenu'
import { HotTableClass } from '@handsontable/react'
import { NodeModel } from '@minoru/react-dnd-treeview'
import { CustomDiscreteSequenceColorPaletteValues } from 'common/types/application'
import { RangeType } from 'handsontable/common'
import { NodeTranslations } from 'pages/code/containers/diagram/types'

export enum ComponentErrorCode {
  component_code = '-1',
  result = '1', // component result
  component_definition = '2',
  node_result = '3'
}

export type InterfaceComponent = {
  component_id: string
  node_id?: string
  visible_in_filter_component?: boolean
  error_code?: ComponentErrorCode
  error_detail?: string
  x?: number
  y?: number
  h?: number
  w?: number
  isLoading?: boolean
  node_title_translation?: NodeTranslations
  has_description: boolean
  can_undo?: boolean
  can_redo?: boolean
  can_save_into_scenario?: boolean
  can_undo_code?: boolean
  can_redo_code?: boolean
  previous_code?: string
}
export enum ButtonFormat {
  default = 'default',
  compact = 'compact'
}
export enum ComponentTypes {
  Core_Node = 'core.Node',
  Core_Index = 'core.Index',
  Core_Selector = 'core.Selector',
  Core_Chart = 'core.Chart',
  Core_PlotlyChart = 'core.PlotlyChart',
  Core_InputValue = 'core.InputValue',
  Core_Table = 'core.Table',
  Core_InputDataArray = 'core.InputDataArray',
  Core_InputDataFrame = 'core.InputDataFrame',
  Core_Html = 'core.Html',
  Core_Menu = 'core.Menu',
  Core_Empty = 'core.Empty',
  Core_Generic = 'core.Generic',
  Core_Indicator = 'core.Indicator',
  Core_Choice = 'core.Choice',
  Core_Button = 'core.Button',
  Core_Form = 'core.Form',
  Core_DashApp = 'core.DashApp',
  Core_InputData = 'core.InputData',
  Core_MatplotlibChart = 'core.MatplotlibChart',
  Core_Assistant = 'core.Assistant',
  Core_MyTasks = 'core.MyTasks',
  Core_ScheduledTasks = 'core.ScheduledTasks',
  Core_Notifications = 'core.Notifications',
  Core_DynamicHtml = 'core.DynamicHtml',
  Core_InputCube = 'core.InputCube',
  Core_Filter = 'core.Filter',
  Core_UploadManager = 'core.UploadManager',
  Core_HtmlVisualization = 'core.HtmlVisualization'
}

export type actionCallback = (action: 'save' | 'cancel') => void

export type SelectOption = {
  value: string
  label: string
}

export type ComponentTitle = {
  custom_text: string
  enabled: boolean
  text: string
  show_units: boolean
  units_text: string
  translations?: NodeTranslations
}

export interface ColumnsToShow {
  [key: string]: boolean
}

export type CommonProperties = {
  title: ComponentTitle
  code?: string
  custom_code?: boolean
  hide_header?: boolean
  hide_border?: boolean
  hide_maximize_button?: boolean
  hide_data_handling_icon?: boolean
  header_color?: string
  header_background_color?: string
  show_data_handling_area?: boolean
  show_quick_pivot_area?: boolean
  show_component_settings_icon?: boolean
}

type PageSize = {
  rows: number
  columns: number
}

type PageInfoItem = {
  current_page: number
  from: number
  to: number
  total: number
}

type PageInfo = {
  columns: PageInfoItem
  rows: PageInfoItem
}
export enum PivotAggEnum {
  sum = 'sum',
  avg = 'avg',
  max = 'max',
  min = 'min',
  count = 'count',
  none = ''
}

export type Pivot = {
  agg: { [key: string]: PivotAggEnum }
  params: { [key: string]: string | string[] | boolean | undefined }
}

type HierarchyValues = {
  field: string
  title?: string
}

export type UnlinkedIndexItem = {
  field: string
  title?: string
}

export enum ColumnDataType {
  number = 'number',
  date = 'date',
  calc = 'calc',
  calc_item = 'calc_item',
  other = 'other'
}

export type Hierarchy = {
  index_id?: string
  index_title?: string
  values: HierarchyValues[]
}

export interface ExtendedHierarchy extends Hierarchy {
  title?: string
}

export type ColumnsDetailItemVisualizationOptions = {
  can_pivot: boolean
  can_filter: boolean
  can_change_aggregation: boolean
  can_change_hierarchy: boolean
  can_edit_field: boolean
  can_apply_subtotals: boolean
  can_sort_values: boolean
}

export type ColumnDetailItem = {
  field: string
  title: string | undefined
  description: string | undefined
  data_type?: ColumnDataType
  selected_label: string | undefined
  selected_values: (string | number)[]
  hierarchy: Hierarchy
  system?: boolean
  source?: string
  visualization_options: ColumnsDetailItemVisualizationOptions
}

export interface DrilldownOptionsInfo {
  open: boolean
  componentId: string
  options: ColumnDetailItem[]
  drilldowns: DrilldownLevel[]
  indexes: { [key: string]: string }
  posX?: number
  posY?: number
  scrollX?: number
  scrollY?: number
}

export enum ColumnPivotItemType {
  base = 'base',
  advanced = 'advanced'
}
export enum ColumnPivotItemSource {
  index = 'index',
  measure = 'measure',
  all = 'all',
  values = 'values',
  boolean = 'boolean'
}

export enum PivotItemColumnType {
  dimension = 'dimension',
  measure = 'measure',
  any = 'any'
}

export type ColumnPivotItem = {
  code: string
  title: string
  multiselect: boolean
  source: ColumnPivotItemSource
  type: ColumnPivotItemType
  column_type: PivotItemColumnType
  values: string[]
}

type ColumnsDetailsPivot = {
  params: ColumnPivotItem[]
}

export interface ValueSelectorOption {
  value: string
  label: string
}

export interface SelectorItemProps {
  columnPivotItem: ColumnPivotItem
  component: PivoteableComponent
  eventCallback: (triggerData: InterfaceEvent) => void
}

type IndexDetailOptions = {
  show_pivot_icon: boolean
  show_filter_icon: boolean
  can_create_calc_field: boolean
  can_create_calc_item: boolean
}

export type ColumnsDetails = {
  columns: ColumnDetailItem[]
  pivot: ColumnsDetailsPivot
  options: IndexDetailOptions
  error: string
}

export interface ActiveTabComponent {
  [key: string]: GenericComponent
}

export interface ApplicationComponents {
  [key: string]: ActiveTabComponent
}

export interface IBaseComponent extends InterfaceComponent {
  component_type: ComponentTypes
  properties: CommonProperties
  result: unknown
  renderKey: string
}

export interface IMinBaseComponent {
  component_id: string
  component_type: ComponentTypes
  h?: number
  w?: number
  x?: number
  y?: number
  renderKey?: string
  properties: CommonProperties
}

export interface IRenderComponent extends Omit<IBaseComponent, 'properties'> {
  properties: TableProperties
}

export enum ValueFormatType {
  Number = 'number',
  Percent = 'percent',
  Date = 'date',
  Datetime = 'datetime',
  General = 'general'
}

export enum ValueFormatPreset {
  Decimal = 'decimal',
  Integer = 'integer',
  Currency = 'currency',
  CurrencyRounded = 'currencyRounded',
  Accounting = 'accounting',
  AccountingRounded = 'accountingRounded',
  Finance = 'finance',
  FinanceRounded = 'financeRounded',
  Percent = 'percent',
  Date = 'date',
  Datetime = 'datetime',
  General = 'general'
}

export interface ValueFormatProperties {
  type: ValueFormatType
  thousands_separator?: boolean
  decimal_places?: number | null
  prefix?: string
  suffix?: string
  date_format?: string
  show_negative_numbers_in_parentheses?: boolean
  [key: string]: any
}
export type ValueFormatPresets = {
  [key in ValueFormatPreset]?: ValueFormatProperties
}

export type GenericComponent =
  | IIndex
  | ITable
  | IChart
  | IHTML
  | IMenu
  | IInputValue
  | ISelector
  | IChoice
  | IEmpty
  | IIndicator
  | IBaseComponent
  | IGeneric
  | IButton
  | IDashApp
  | IMatplotlibChart
  | IAssistant
  | IMyTask
  | IScheduledTasks
  | INotifications
  | IDynamicHTML
  | IFilterComponent
  | IUploadManager
  | IHtmlVisualization

type Orientation = 'horizontal' | 'vertical'

export interface ProgressBarProperties {
  max_value: number
  min_hue: number
  max_hue: number
  saturation: number
  lightness: number
  show_value: boolean
  [key: string]: any
}

export enum HeatmapType {
  total = 'total',
  row = 'row',
  column = 'column'
}

export enum HeatmapColor {
  red_green = 'red_green',
  red_green_alt = 'red_green_alt',
  green_red = 'green_red',
  green_red_alt = 'green_red_alt',
  red_white = 'red_white',
  white_red = 'white_red',
  white_green = 'white_green',
  green_white = 'green_white',
  green_blue = 'green_blue',
  blue_green = 'blue_green',
  red_blue = 'red_blue',
  blue_red = 'blue_red',
  custom = 'custom'
}

export interface HeatmapProperties {
  by: HeatmapType
  heatmap_color?: HeatmapColor
  hex_base_background_color?: string
  hex_text_color?: string
  exclude_nans?: boolean
  exclude_right_total?: boolean
  exclude_bottom_total?: boolean
  [key: string]: any
}

// Index //

export enum IndexFormats {
  Default = 'default',
  Small = 'small',
  Hidden = 'hidden',
  Range = 'range',
  Checkboxes = 'checkboxes',
  Select = 'select',
  OptionsList = 'options_list'
}

export interface IndexProperties extends CommonProperties {
  single_select: boolean
  format: IndexFormats
  orientation: Orientation
  applied_hierarchy?: string
  range_hide_min_max_labels?: boolean
  build_automatic_date_hierarchies?: boolean | null
}

export type IndexResult = {
  field_id: string
  title: string
  selected_values: Array<string | number>
  type: string
  hierarchy?: Hierarchy
  all_selected?: boolean
  index_values?: Array<string | number>
  format?: IndexFormats
}

export interface IIndex extends InterfaceComponent {
  component_type: ComponentTypes.Core_Index
  properties: IndexProperties
  result: IndexResult
  renderKey?: string
}

export type FilterComponentMetadata = {
  component_type?: ComponentTypes
  result?: {
    result_type?: string
    type?: string
    columns_details?: {
      columns?: Array<{
        field: string
        data_type?: ColumnDataType
      }>
    }
    condition_filters?: ColumnConditionFilters
    node_filters?: ColumnNodeFilters
  }
}

export type GetInterfaceValuesMethod = (
  interfaceId: string,
  componentId: string,
  indexId: string,
  paginateResults: boolean,
  page?: number,
  filter?: IndexFilterChoices | null,
  text1?: string | null,
  page_size?: number
) => Promise<InterfaceIndexValuesData>

export type GetNodeInterfaceValuesMethod = (
  nodeId: string,
  componentId: string,
  indexId: string,
  paginateResults: boolean,
  page?: number,
  filter?: IndexFilterChoices | null,
  text1?: string | null,
  page_size?: number
) => Promise<InterfaceIndexValuesData>

export interface IIndexSubcomponent {
  interfaceNodeId: string
  componentId: string
  nodeId: string
  fieldId: string
  eventCallback: (triggerData: InterfaceEvent) => void
  singleSelect: boolean
  initSelValues: (string | number)[]
  getValuesMethod: GetInterfaceValuesMethod | GetNodeInterfaceValuesMethod
  initIndexValues: (string | number)[]
  context?: Context
}
export interface IIndexRange extends IIndexSubcomponent {
  component: IIndex
}
export interface IIndexBox extends IIndexSubcomponent {
  type: 'chips' | 'checkboxes'
  orientation: Orientation
  isSmall: boolean
}
export interface IIndexSelector extends IIndexSubcomponent {
  component: IIndex
  label?: string
  handleChangeCustom?: (value: string) => void
  disableTrigger?: boolean
}

export interface IIndexHidden {
  component: IIndex
  eventCallback: (triggerData: InterfaceEvent) => void
  editMode: boolean
  context?: Context
  initSelValues: (string | number)[]
}

export interface ISelectHidden {
  component: ISelectorComponent
  eventCallback: (triggerData: InterfaceEvent) => void
  editMode: boolean
  context?: Context
  initSelValues: (string | number)[]
  getOptionsAndIndexMethod: GetOptionsAndIndexMethod
  multiselect: boolean
  timeoutMs: number
  title: string
  initialIndexesAndValues: IndexesAndOptions
}
// Table //

interface TableColumns extends Handsontable.GridSettings {
  conditional_format?: ConditionalFormatProperties[]
}

export type DrilldownIndexes = {
  [key: string]: string
}

export interface DrilldownLevel {
  indexes: DrilldownIndexes
  drilldown_to: string
}

export enum HideEmptyDataOptions {
  Row = 'row',
  Column = 'column',
  Both = 'both',
  None = ''
}

export enum TableTotalPosition {
  End = 'end',
  Beginning = 'beginning'
}

export enum TableAlignmentEnum {
  left = 'left',
  center = 'center',
  right = 'right'
}

export enum TableTotalType {
  Right = 'right',
  Bottom = 'bottom'
}

export enum TableStyleFilters {
  equal = 'equal',
  notequal = 'notequal',
  contains = 'contains',
  notcontains = 'notcontains',
  begin_with = 'begin_with',
  not_begin_with = 'not_begin_with',
  equal_to_node_values = 'equal_to_node_values',
  regular_expression = 'regular_expression'
}

export enum TableStyleConditionType {
  index = 'index',
  column = 'column',
  row = 'row'
}

export enum TableStyleConditionApplyTo {
  all = 'all',
  values = 'values',
  labels = 'labels'
}

export interface TableStyleCondition {
  condition_type: TableStyleConditionType
  dimension_name: string
  values: string[]
  filter_type: TableStyleFilters
  apply_to: TableStyleConditionApplyTo
}

export interface WizardTableStyleCondition extends TableStyleCondition {
  id: number
}

export interface TableCellProperties {
  value_format: ValueFormatProperties
  style: CSSProperties
  conditional_format?: ConditionalFormatProperties[]
  progress_bar?: ProgressBarProperties
  heatmap?: HeatmapProperties
  readOnly?: boolean
  fixed_column_width: boolean
  fixed_column_width_value?: number
}

export interface TableCustomStyle {
  conditions: TableStyleCondition[]
  cell_properties: TableCellProperties
}

export interface TableStyle {
  all?: TableCellProperties
  custom?: TableCustomStyle[]
}

export enum CalculatedFieldType {
  expression = 'expression',
  function = 'function'
}

export interface CalculatedField {
  name: string
  calc_type: CalculatedFieldType
  value: string
}

export enum CalculatedItemType {
  custom = 'custom',
  subtotal = 'subtotal',
  subtotal_by_element = 'subtotal_by_element'
}

export interface CalculatedItem {
  dimension: string
  name: string
  expression: string
  item_type?: CalculatedItemType
  include_single_element_segments?: boolean
  aggregation_method?: PivotAggEnum
}

export enum ScenarioCalculationType {
  Values = 'Values',
  Difference = 'Difference',
  PercentageDifference = 'PercentageDifference'
}

export enum ScenarioPlacement {
  Rows = 'rows',
  Columns = 'columns'
}

export interface ScenarioCalculation {
  calculation_type: ScenarioCalculationType
  end_text: string
}

export interface ScenariosProperties {
  compare: boolean
  calculations: ScenarioCalculation[]
  placement: ScenarioPlacement
  fill_missing_with_zero?: boolean
  zero_division_returns_zero?: boolean
}

export enum ComponentResultType {
  Other = 'other',
  PandasDataFrame = 'pandas_dataframe',
  PolarsDataFrame = 'polars_dataframe'
}

export enum ComponentProcessingEngine {
  Default = 'default',
  Pandas = 'pandas',
  Polars = 'polars'
}

export interface AppliedHierarchy {
  field: string
  aggregation_method?: PivotAggEnum | undefined | null
}

export interface TableProperties extends CommonProperties {
  table_properties: Handsontable.GridSettings | undefined
  columns: { string: TableColumns }
  renderers: { [key: string]: string }
  page_size: PageSize
  sort: { [key: string]: string }
  pivot: Pivot
  drilldowns?: DrilldownLevel[]
  right_total: boolean
  right_total_position: TableTotalPosition | null
  right_total_aggregation_method: PivotAggEnum | null
  bottom_total: boolean
  bottom_total_position: TableTotalPosition | null
  bottom_total_aggregation_method: PivotAggEnum | null
  total_aggregation_overrides?: TotalAggregationOverrides
  subtotal_label?: string | null
  hide_empty_data: HideEmptyDataOptions
  styles: TableStyle
  calculated_fields: CalculatedField[]
  calculated_items: CalculatedItem[]
  scenarios: ScenariosProperties
  show_column_filter_icon: boolean
  hide_footer: boolean
  unlinked_indexes: UnlinkedIndexItem[]
  show_index?: boolean
  processing_engine: ComponentProcessingEngine | null
  fixed_columns: number | null
  applied_hierarchies: { [key: string]: AppliedHierarchy }
  show_view_as_table_chart_icon: boolean
  custom_sorts?: CustomSorts
  table_alignment?: TableAlignmentEnum | null
  build_automatic_date_hierarchies?: boolean | null
}

interface CustomSorts {
  [key: string]: string[]
}

// Per-dimension, per-member aggregation method for totals:
// { dimensionField: { memberValue: PivotAggEnum } }. Defined at the dimension
// level (not per total): it feeds the bottom total when the dimension is in
// columns and the right total when it is in rows, and is kept when the
// dimension is moved between axes or hidden.
// The reserved key "columns" holds per-field methods for the bottom total,
// keyed by value-column/field name (a form's columns are its fields).
export interface TotalAggregationOverrides {
  [dimensionFieldOrColumns: string]: { [memberOrFieldValue: string]: PivotAggEnum }
}

interface NumericFormat {
  pattern: NumbroNumericPattern
}

export interface TableDataCellProperties {
  style?: CSSProperties
  conditional_format?: ConditionalFormatProperties[]
  progress_bar?: ProgressBarProperties
  numericFormat?: NumericFormat
  prefix?: string
  suffix?: string
  dateRendererFormat?: string
  renderer?: string
  readOnly?: boolean
  shape?: Shape
  [key: string]: any
}

export interface TableDataCellsProperties {
  apply_to: TableStyleConditionApplyTo
  cell_properties: TableDataCellProperties
}

type TableData = {
  headers: any
  columns: Handsontable.ColumnSettings[]
  values: Array<{ string: any }>
  dynamic_properties: Handsontable.GridSettings | undefined
  column_keys: Array<Array<any>>
  edit_mode: boolean
  allow_add_rows: boolean
  allow_remove_rows: boolean
  page_info: PageInfo
  blocked_rows: number[]
  non_drilldownable_rows: number[]
  applied_drilldown_indexes: string[]
  drilldowneable_indexes: string[]
  cells_properties: { [key: string]: TableDataCellsProperties[] }
  is_pivot: boolean
  hidden_columns: number[]
  number_of_dimension_columns: number
}

export interface TableFilters {
  [key: string]: string[]
}

export enum ConditionFilterCombinator {
  And = 'and',
  Or = 'or'
}

export interface ConditionFilterRule {
  choice: IndexFilterChoices
  value1: string
  value2?: string | null
}

// Stores the conditions themselves, not a values snapshot like TableFilters. Read it through
// getConditionFilterRules: filters saved before `rules` existed only carry choice/value1/value2.
export interface ColumnConditionFilter {
  choice?: IndexFilterChoices | null
  value1?: string | null
  value2?: string | null
  combinator?: ConditionFilterCombinator
  rules?: ConditionFilterRule[]
}

export type ColumnConditionFilters = { [field: string]: ColumnConditionFilter }

// The node whose result is read on every render to pick the column's values.
export interface ColumnNodeFilter {
  node_id: string
}

export type ColumnNodeFilters = { [field: string]: ColumnNodeFilter }

export type TableResult = {
  data: TableData
  columns_details: ColumnsDetails
  filters: TableFilters
  condition_filters: ColumnConditionFilters
  node_filters?: ColumnNodeFilters
  has_scenarios: boolean
  indexes: string[]
  result_type?: ComponentResultType
}

export interface ITable extends InterfaceComponent {
  component_type:
    | ComponentTypes.Core_Table
    | ComponentTypes.Core_InputDataArray
    | ComponentTypes.Core_InputCube
    | ComponentTypes.Core_InputDataFrame
    | ComponentTypes.Core_Form
  properties: TableProperties
  result: TableResult
}

export type TableChange = {
  keys: { [key: string]: any }
  old_value?: any
  new_value: any
}

export type TableChangePageData = {
  rows_from: number
  columns_from: number
}

export type FormChange = {
  row_id: number | null
  column_name: string
  value: any
  hot_row_id: number
}

// Chart //

type ListenEventChart = {
  [key: string]: {
    event: 'HoverEvent' | 'SelectedEvent'
    component_id: string
  }
}

export type ChartLayoutFinalProp = {
  [key: string]: string | number | boolean
}
export type ChartLayoutLegend = {
  orientation?: 'h' | 'v'
  xanchor?: 'auto' | 'left' | 'center' | 'right'
  x?: number
  yanchor?: 'auto' | 'top' | 'middle' | 'bottom'
  y?: number
  [key: string]: any
}
export type ChartLayoutMargins = {
  t?: number
  r?: number
  b?: number
  l?: number
  [key: string]: any
}
export type ChartLayout = {
  [key: string]:
    | string
    | number
    | boolean
    | ChartLayoutFinalProp
    | ChartLayoutLegend
    | ChartLayoutMargins
    | any
}

export type ChartPropertiesProps = {
  [key: string]: string | number | undefined
}

export interface LayoutComponentProps {
  component: IChart
  triggerUpdateProperties: (
    component_id: string,
    propsToUpdate: {
      [key: string]: any
    }
  ) => void
  interfaceId: string
  context: Context
  noLayoutSection?: boolean
  size?: 'small' | 'medium'
}

export interface StyleYAxisRangeProperties extends LayoutComponentProps {
  propertyName: 'y_axis_range' | 'y_axis_range_secondary_axis'
}

export type LayoutSelectOption = {
  code: string
  title: string
}

export enum ChartUniformTextMode {
  none = 'none',
  false = 'false',
  hide = 'hide',
  show = 'show'
}
export enum ChartTextPosition {
  none = 'none',
  inside = 'inside',
  outside = 'outside'
}
export type ChartLabels = {
  field?: string
  text_position?: ChartTextPosition
  min_size?: number | ''
  mode?: ChartUniformTextMode
}

export type ChartYAxisRange = {
  min?: number
  max?: number
}

export interface ChartStyleProperties {
  value_format: ValueFormatProperties
  value_format_secondary_axis?: ValueFormatProperties
  color_discrete_sequence?: string
  color_continuous_scale?: string
  custom_series_colors_name?: string
  labels?: ChartLabels
  labels_secondary_axis?: ChartLabels
  y_axis_range?: ChartYAxisRange
  y_axis_range_secondary_axis?: ChartYAxisRange
  combined_primary_axis_color?: string
  combined_secondary_axis_color?: string
  show_y_axis_title?: boolean
  show_x_axis_title?: boolean
}

export enum ValueFormatCodes {
  value_format = 'value_format',
  value_format_secondary_axis = 'value_format_secondary_axis'
}

export enum LabelsCodes {
  labels = 'labels',
  labels_secondary_axis = 'labels_secondary_axis'
}

export interface ChartStyle {
  all?: ChartStyleProperties
  custom?: any[]
}

export enum ChartMainType {
  column = 'column',
  bar = 'bar',
  line = 'line',
  pie = 'pie',
  area = 'area',
  scatter = 'scatter',
  combined = 'combined',
  others = 'others'
}

export enum PlotlyImportSource {
  px = 'px',
  go = 'go',
  combined = 'combined'
}

export interface ChartType {
  main_type: ChartMainType
  subtype: string
  plotly_import_source: PlotlyImportSource
  plotly_code: string
  chart_params: { [key: string]: any }
  chart_layout: { [key: string]: any }
}

export type ChartTypeProperties = {
  plotlyImportSource: PlotlyImportSource
  code: string
  label: string
  chartProperties: { [key: string]: any }
  layoutProperties: { [key: string]: any }
  chartUpdateTraces: { [key: string]: any }
}

export type ChartTypeGroupProperties = {
  [key: string]: ChartTypeProperties
}

export interface ChartProperties extends CommonProperties {
  chart_type: ChartType
  chart_properties: ChartPropertiesProps
  chart_layout: ChartLayout
  selected_event: boolean
  hover_event: boolean
  hover_select_targets: string[]
  relayout_event: boolean
  drilldown_event: boolean
  listen: ListenEventChart
  pivot: Pivot
  page_size: PageSize
  drilldowns: DrilldownLevel[]
  hide_empty_data: boolean
  hide_empty_legend: boolean
  sync_y_axes_range: boolean
  styles: ChartStyle
  calculated_fields: CalculatedField[]
  calculated_items: CalculatedItem[]
  scenarios: ScenariosProperties
  sort: { string: string }
  unlinked_indexes: UnlinkedIndexItem[]
  processing_engine: ComponentProcessingEngine | null
  applied_hierarchies: { [key: string]: AppliedHierarchy }
  show_view_as_table_chart_icon: boolean
  custom_sorts?: CustomSorts
  build_automatic_date_hierarchies?: boolean | null
}

export enum ColorScaleSource {
  plotly = 'plotly',
  custom = 'custom',
  pyplan = 'pyplan'
}

export interface ColorScale {
  code: string
  title: string
  values: CustomDiscreteSequenceColorPaletteValues[]
  source: ColorScaleSource
}

export interface ChartExtraProperties {
  is_color_property_numeric: boolean
  color_scales: ColorScale[]
  must_aggregate: boolean
  available_drilldown_indexes: string[]
  supported_types_for_drilldown: string[]
}

export type ChartResult = {
  figure: Figure
  columns_details: ColumnsDetails
  filters: TableFilters
  condition_filters: ColumnConditionFilters
  node_filters?: ColumnNodeFilters
  extra_properties: ChartExtraProperties
  has_scenarios: boolean
  result_type?: ComponentResultType
}

export interface IChart extends InterfaceComponent {
  component_type: ComponentTypes.Core_Chart | ComponentTypes.Core_PlotlyChart
  properties: ChartProperties
  result: ChartResult
}

// HTML //

export interface HTMLProperties extends CommonProperties {
  html: string
  isolate_styles: boolean
}
type HTMLResult = {
  html: string
}

export interface IHTML extends InterfaceComponent {
  component_type: ComponentTypes.Core_Html
  properties: HTMLProperties
  result: HTMLResult
}

// Dynamic HTML //

export enum DynamicHtmlActionTypes {
  OPEN_INTERFACE = 'open_interface',
  GO_TO_SECTION = 'go_to_section',
  RUN_APP_NODE = 'run_app_node',
  RUN_APP_NODE_WITH_PARAMETER = 'run_app_node_with_parameter',
  ATTRIBUTE_FROM_NODE = 'attribute_from_node',
  CHILDREN_FROM_NODE = 'children_from_node'
}

interface ActionTypeValue {
  interfaceName?: string
  interfaceId?: string
  openInNewTab?: boolean
  assistantId?: string
  section?: string
  customPathSection?: string
  nodeId?: string
  nodeTitle?: string
  nodePreview?: string
  // Supports static HTML attributes, 'value', or 'selectedOption:<attribute>' for selects.
  sourceAttribute?: string
  functionParameter?: string
  parameterValue?: string | null
}

export interface DynamicHtmlActionTableData {
  attribute: string
  attributeValue: string
  actionType: DynamicHtmlActionTypes
  actionTypeValue: ActionTypeValue
  targetAttribute?: string
}

interface DynamicHTMLProperties extends CommonProperties {
  html: string
  actionTableData?: DynamicHtmlActionTableData[]
}

type DynamicHTMLResult = {
  html: string
  actionTableData?: DynamicHtmlActionTableData[]
  iframe_id?: string
}

export interface IDynamicHTML extends InterfaceComponent {
  component_type: ComponentTypes.Core_DynamicHtml
  properties: DynamicHTMLProperties
  result: DynamicHTMLResult
}

// Data HTML //

export interface HtmlVisualizationProperties extends CommonProperties {}

type HtmlVisualizationResult = {
  html: string
  columns_details: ColumnsDetails
  filters: TableFilters
}

export interface IHtmlVisualization extends InterfaceComponent {
  component_type: ComponentTypes.Core_HtmlVisualization
  properties: HtmlVisualizationProperties
  result: HtmlVisualizationResult
}

// Filter //

export type FilterComponent = ISelector | IChoice | IIndex

export interface FilterComponentResult {
  filter_components: FilterComponent[]
}

export interface VisibilityControllerNodeReference {
  id: string
  label: string
}

export interface FilterComponentProperties extends CommonProperties {
  filter_components: FilterComponent[]
  position_sticky: boolean
  fixed_position_on_top: boolean
  visibility_controller_node_id?: VisibilityControllerNodeReference | string | null
}
export interface IFilterComponent extends InterfaceComponent {
  component_type: ComponentTypes.Core_Filter
  properties: FilterComponentProperties
  result: FilterComponentResult
}

export interface FilterComponentDialogProps {
  component: IFilterComponent
  interfaceId: string
  openFilterComponentDialog: boolean
  handleCloseFilterComponentDialog: (revert?: boolean) => void
  editMode: boolean
  filterToEdit?: string
}

export enum GoToSectionValues {
  INTERFACES = '/interfaces',
  TASKS = '/tasks',
  CODE = '/code',
  HOME = '/',
  APPS = '/apps',
  FILES = '/files',
  SCENARIO_MANAGER = '/scenarios/scenario-manager',
  SCENARIO_TEMPLATES = '/scenarios/scenario-templates',
  PROCESS = '/process',
  VERSIONS = '/versions',
  APP_CONSOLIDATION = '/app-consolidation',
  INTERFACE_LINKS = '/interface-links',
  API_ENDPOINTS = '/api-endpoints',
  INSTANCES = '/instances',
  LOGS = '/logs',
  GENERAL_SETTINGS = '/general-settings',
  USERS = '/users',
  TEAMS = '/teams',
  DEPARTMENTS = '/departments',
  ROLES = '/roles',
  PERMISSIONS_BY_ROLE = '/permissions-by-role',
  COMPANIES = '/companies'
}

export interface NavigateToPathsTypes {
  value: GoToSectionValues
  label: string
}

export enum TableAttributes {
  Attribute = 'attribute',
  Value = 'value',
  TargetAttribute = 'targetAttribute'
}

// Button //

export enum ButtonStyleCheckboxProperties {
  multiline = 'multiline',
  uppercase = 'uppercase'
}

export interface ButtonIconProperties extends Shape {
  styles: CSSProperties
}
export interface ButtonStyleProperties {
  style: CSSProperties
  multiline: boolean
  uppercase: boolean
  format: null | ButtonFormat
  icon_properties: ButtonIconProperties | null
}

export interface ButtonStyle {
  all?: ButtonStyleProperties
  custom?: any[]
}

export interface ButtonProperties extends CommonProperties {
  styles: ButtonStyle
  show_confirmation_dialog: boolean
  confirmation_dialog_message?: string
  disabled?: boolean
}

// Menu //

export enum MenuFormat {
  accordion = 'accordion',
  boxes = 'boxes',
  compact_menu = 'compact_menu'
}

export enum MenuActionType {
  INTERFACE = 'interface',
  NAVIGATE = 'navigate'
}

export interface MenuTranslation {
  text: NodeTranslations
  subtitle: NodeTranslations
}

export type MenuTranslationKey = keyof MenuTranslation

export type MenuAction = {
  id: string | number
  pos: number
  text?: string
  action_type: MenuActionType
  icon?: IconDefinition
  reference?: string
  target?: string
  subtitle?: string
  disabled?: boolean
  translations?: MenuTranslation
}

export type MenuItem = {
  icon?: IconDefinition
  actions?: MenuAction[]
  open_at_startup?: boolean
  subtitle?: string
  translations?: MenuTranslation
}

export type Menu = {
  items: NodeModel<MenuItem>[]
}

type MenuResult = {
  menu: Menu
}
export type AlignmentItems = 'center' | 'right' | 'left'
export interface MenuProperties extends CommonProperties {
  format: MenuFormat
  menu: Menu
  items_per_row: number
  align_items: AlignmentItems
  auto_open_single_action?: boolean
  show_search_input?: boolean
  background_color: string | null
  icon_color: string | null
  hover_color: string | null
  text_color: string | null
}

export interface IMenu extends InterfaceComponent {
  component_type: ComponentTypes.Core_Menu
  properties: MenuProperties
  result: MenuResult
}

// Selector //

export enum SelectorFormats {
  Default = 'default',
  Select = 'select',
  Checkbox = 'checkbox',
  Slider = 'slider',
  OptionsList = 'options_list',
  SelectableChips = 'selectable_chips',
  DatePicker = 'datepicker',
  Hidden = 'hidden'
}

export enum SelectorSelectionDomain {
  Range = 'range',
  ExplicitDates = 'explicit_dates'
}

export interface SelectorProperties extends CommonProperties {
  format: SelectorFormats | undefined
  orientation?: Orientation
  page_size?: number
  range_hide_min_max_labels?: boolean
}

export type SelectorItem = {
  value: string
  index: number
}

export type SelectorIndexesAndOptions = {
  [key: number]: string
}

export type SelectorAndIndexes = {
  value: string
  index: number
}

export enum SelectorView {
  Year = 'year',
  Month = 'month',
  Day = 'day'
}

export type SelectorResult = {
  selected_indexes: number[]
  selected_values: string[]
  multiselect: boolean
  include_all: boolean
  all_selected: boolean
  save_selected_labels: boolean
  options_and_indexes?: SelectorAndIndexes[]
  format: SelectorFormats
  selection_domain?: SelectorSelectionDomain
  start_date?: string | null
  end_date?: string | null
  allowed_dates?: string[]
  display_date_format?: string
  views?: SelectorView[]
  open_to?: SelectorView
  type: SelectorResultType
}

export interface ISelector extends InterfaceComponent {
  component_type: ComponentTypes.Core_Selector
  properties: SelectorProperties
  result: SelectorResult
  renderKey?: string
}

// Choice //

interface ChoiceProperties extends CommonProperties {
  format: 'select' | 'radio' | 'slider' | undefined
  orientation?: Orientation
  page_size?: number
  range_hide_min_max_labels?: boolean
}

export type ChoiceItem = {
  value: string | number
  text: string | number
}

export type ChoiceIndexesAndOptions = {
  [key: number]: string
}

type ChoiceResult = {
  selected_indexes: number[]
  selected_values: string[]
  multiselect: boolean
  include_all: boolean
  all_selected: boolean
  save_selected_labels: boolean
  format: 'select' | 'radio' | 'slider' | undefined
  type: SelectorResultType
}

export interface IChoice extends InterfaceComponent {
  component_type: ComponentTypes.Core_Choice
  properties: ChoiceProperties
  result: ChoiceResult
  renderKey?: string
}

// InputValue //

export interface InputValueResult {
  value: string | number
  is_editable: boolean
  format: IndicatorFormat
}

export interface InputValueStyleProperties {
  value_format: ValueFormatProperties
  hide_characters: boolean
  conditional_format?: ConditionalFormatProperties[]
}

export interface InputValueStyle {
  all?: InputValueStyleProperties
  custom?: any[]
}
export enum InputValueDataType {
  Float = 'Float',
  Integer = 'Integer',
  String = 'String',
  Any = 'Any'
}
export enum InputValueRule {
  Between = 'Between',
  NotBetween = 'Not between',
  EqualTo = 'Equal to',
  NotEqualTo = 'Not equal to',
  GreaterThan = 'Greater than',
  LessThan = 'Less than',
  GreaterThanOrEqualTo = 'Greater than or equal to',
  LessThanOrEqualTo = 'Less than or equal to',
  TextLength = 'Text length',
  Any = 'Any'
}
interface InputValueValidationProperties {
  data_type: InputValueDataType
  rule: InputValueRule
  rule_value1: number
  rule_value2: number
  error_message: string | null
}
interface InputValueProperties extends CommonProperties {
  styles: InputValueStyle
  validation_properties: InputValueValidationProperties
}
export interface IInputValue extends InterfaceComponent {
  component_type: ComponentTypes.Core_InputValue
  properties: InputValueProperties
  result: InputValueResult
}

export interface InputScalarComponentProps {
  nodeId: string
  component?: IInputValue
  initialValue: string
  eventCallback: (triggerData: InterfaceEvent) => void
  classes?: Partial<ClassNameMap<'root'>>
  selected?: boolean
  disableInteractivy?: boolean
  is_editable: boolean
  unformattedValue: number | string
  fromNode?: boolean
}

// Empty //

export interface IEmpty extends InterfaceComponent {
  component_type: ComponentTypes.Core_Empty
  properties: CommonProperties
  result: void
}

// Selector component

export type ISelectorComponent = ISelector | IChoice

export type IndexesAndOptions = SelectorIndexesAndOptions | ChoiceIndexesAndOptions

export type ParamsInfo = SelectorParamsInfo | ChoiceParamsInfo

export type OptionsAndIndexesInfo = SelectorOptionsAndIndexInfo | ChoiceOptionsAndIndexInfo

export type GetParamsMethod = (nodeId: string) => Promise<SelectorParamsInfo | ChoiceParamsInfo>

export type GetOptionsAndIndexParams = {
  nodeId: string
  page_size: number
  text_filter?: string | null
  url?: string | null
}

export type GetOptionsAndIndexMethod = (
  nodeId: string,
  page_size?: number,
  text_filter?: string | null,
  url?: string | null,
  filter_type?: IndexTextFilterChoices | null,
  sort?: 'asc' | 'desc' | null
) => Promise<SelectorOptionsAndIndexInfo | ChoiceOptionsAndIndexInfo>

// SelectWithAutocomplete

export interface SelectWithAutocompleteProps {
  nodeId: string
  componentId: string
  pageSize: number
  timeoutMs: number
  initialIndexesAndValues: IndexesAndOptions
  multiselect: boolean
  orientation?: Orientation
  getOptionsAndIndexMethod: GetOptionsAndIndexMethod
  triggerCallback: (T: SetSelectorValueParams) => Promise<void> | void
  label?: string | ReactNode
  classes?: Partial<ClassNameMap<AutocompleteClassKey>>
  limitTags?: number
  disabled?: boolean
  isMultiSelectFromSelectorNode?: boolean
  saveSelectedLabels?: boolean
  isCalc?: boolean
  fromNode?: boolean
  result?: SelectorResult
}

// SelectWithCheckboxes

export interface SelectWithCheckboxesProps {
  nodeId: string
  componentId: string
  pageSize: number
  initialIndexesAndValues: IndexesAndOptions
  multiselect: boolean
  allSelected: boolean
  orientation?: Orientation
  getOptionsAndIndexMethod: GetOptionsAndIndexMethod
  triggerCallback: (T: SetSelectorValueParams) => Promise<void> | void
  eventCallback: (triggerData: InterfaceEvent) => void
  saveSelectedLabels?: boolean
  result?: SelectorResult
}

// SelectWithSlider

export interface SelectWithSliderProps {
  nodeId: string
  componentId: string
  pageSize: number
  timeoutMs: number
  initialIndexesAndValues: IndexesAndOptions
  multiselect: boolean
  orientation?: Orientation
  getOptionsAndIndexMethod: GetOptionsAndIndexMethod
  triggerCallback: (T: SetSelectorValueParams) => Promise<void> | void
  saveSelectedLabels?: boolean
  hideMinMaxLabels?: boolean
  optionsAndIndexes?: SelectorAndIndexes[]
  result?: SelectorResult
}

// SelectWithDatePicker

export interface SelectWithDatePickerProps {
  componentId: string
  multiselect: boolean
  initialSelectedValues: string[]
  triggerCallback: (T: SetSelectorValueParams) => Promise<void> | void
  disabled?: boolean
  openPickerOnInputClick?: boolean
  stopOpenEventPropagation?: boolean
  saveSelectedLabels?: boolean
  selectionDomain?: SelectorSelectionDomain
  startDate?: string | null
  endDate?: string | null
  allowedDates?: string[]
  displayDateFormat?: string
  views?: SelectorView[]
  openTo?: SelectorView
}

// SelectWithSelectedChips

export interface SelectWithSelectedValuesProps {
  nodeId: string
  componentId: string
  title: string
  timeoutMs: number
  multiselect: boolean
  initialIndexesAndValues: IndexesAndOptions
  getOptionsAndIndexMethod: GetOptionsAndIndexMethod
  eventCallback: (triggerData: InterfaceEvent) => void
  saveSelectedLabels?: boolean
  allSelected?: boolean
  orientation?: Orientation
  isIndex?: boolean
  field?: string
}

// SelectWithClickableChips

export interface SelectWithClickableChipsProps {
  nodeId: string
  componentId: string
  pageSize: number
  multiselect: boolean
  initialIndexesAndValues: IndexesAndOptions
  getOptionsAndIndexMethod: GetOptionsAndIndexMethod
  eventCallback: (triggerData: InterfaceEvent) => void
  allSelected?: boolean
  saveSelectedLabels?: boolean
  orientation: Orientation
}

// Indicator //

export interface Icon {
  prefix: IconPrefix
  name: IconName
}

export interface Shape {
  icon: Icon
  color?: string
  show_icon_only?: boolean
}

export interface ConditionalFormatProperties {
  condition_type: ConditionalFormatType
  value1: any
  value2?: any
  style: CSSProperties
  shape?: Shape
  value_format?: ValueFormatProperties
  numericFormat?: NumericFormat
  prefix?: string
  suffix?: string
  dateRendererFormat?: string
}

export enum ConditionalFormatType {
  Range = 'range',
  EqualTo = 'equal_to',
  NotEqualTo = 'not_equal_to',
  GreaterThan = 'greater_than',
  GreaterThanOrEqualTo = 'greater_than_or_equal_to',
  LessThan = 'less_than',
  LessThanOrEqualTo = 'less_than_or_equal_to',
  Expression = 'expression',
  Function = 'function'
}

export interface IndicatorStyleProperties {
  value_format: ValueFormatProperties
  style: CSSProperties
  conditional_format?: ConditionalFormatProperties[]
  vertical_align?: string | null
}

export interface IndicatorStyle {
  all?: IndicatorStyleProperties
  custom?: any[]
}

export interface IndicatorProperties extends CommonProperties {
  pivot: Pivot
  shape?: Shape
  styles: IndicatorStyle
  unlinked_indexes: UnlinkedIndexItem[]
  processing_engine: ComponentProcessingEngine | null
  applied_hierarchies: { [key: string]: AppliedHierarchy }
  build_automatic_date_hierarchies?: boolean | null
}

interface NumbroNumericPattern {
  thousandSeparated?: boolean
  mantissa?: number
  output?: 'percent' | 'byte' | 'ordinal' | 'time'
  forceSign?: boolean
  optionalMantissa?: boolean
  trimMantissa?: boolean
  spaceSeparated?: boolean
  negative?: 'parenthesis'
  average?: boolean
  totalLength?: number
}

interface IndicatorFormat {
  type?: ValueFormatType
  pattern?: NumbroNumericPattern
  prefix?: string
  suffix?: string
  date_format?: string
}

export interface IndicatorResult {
  value: string | number
  columns_details: ColumnsDetails
  filters: TableFilters
  format: IndicatorFormat
}

export interface IIndicator extends InterfaceComponent {
  component_type: ComponentTypes.Core_Indicator
  properties: IndicatorProperties
  result: IndicatorResult
}

// Generic

type GenericType =
  | 'string'
  | 'html'
  | 'url'
  | 'number'
  | 'list'
  | 'tuple'
  | 'set'
  | 'dict'
  | 'other'

type GenericValue = string | number | any[] | object

interface GenericResult {
  type: GenericType
  value: GenericValue
}

export interface IGeneric extends InterfaceComponent {
  component_type: ComponentTypes.Core_Generic
  properties: CommonProperties
  result: GenericResult
}
interface ButtonResult {
  node_id: string
  title: string
}

export interface IButton extends InterfaceComponent {
  component_type: ComponentTypes.Core_Button
  properties: ButtonProperties
  result: ButtonResult
}

export type EveryComponentProperty =
  | IndexProperties
  | TableProperties
  | ChartProperties
  | HTMLProperties
  | MenuProperties
  | SelectorProperties
  | ChoiceProperties
  | IndicatorProperties
  | ButtonProperties
  | CommonProperties

export interface ComponentToCreate {
  component: ComponentTypes
  position: { x: number; y: number; w?: number; h?: number }
}

// Form

interface FormData extends TableData {
  has_unconfirmed_changes: boolean
  must_scroll_down: boolean
  must_scroll_up: boolean
  allow_row_reordering: boolean
  validate_selectors_on_initial_render?: boolean
  track_history?: boolean
}

export interface FormResult extends TableResult {
  data: FormData
  columns_details: ColumnsDetails
  filters: TableFilters
  has_write_permissions?: boolean
}

export enum NewRowsPosition {
  Beginning = 'beginning',
  End = 'end'
}

export interface FormProperties extends TableProperties {
  hide_id_column: boolean
  n_rows_to_add: number
  confirm_on_change?: boolean
  invalidate_outputs_on_change?: boolean
  invalidate_outputs_on_confirm?: boolean
  update_calc_columns_on_change?: boolean
  update_calc_columns_on_confirm?: boolean
  show_select_columns_icon?: boolean
  selected_column_fields?: string[]
  selected_columns_condition_filter?: ColumnConditionFilter | null
  selected_columns_node_filter?: ColumnNodeFilter | null
  show_edit_excel_icon?: boolean
  show_refresh_icon?: boolean | null
  show_history_icon?: boolean
  show_view_as_table_chart_icon: boolean
  selector_max_options?: number
  new_rows_position?: NewRowsPosition
}

export interface IForm extends InterfaceComponent {
  component_type: ComponentTypes.Core_Form
  properties: FormProperties
  result: FormResult
}

export type GetFormColumnsParams = {
  nodeId: string
  page_size: number
  text_filter?: string | null
  url?: string | null
}

export type GetNodeFormColumnsMethod = (
  nodeId: string,
  componentId: string,
  paginateResults: boolean,
  page?: number,
  filter?: IndexTextFilterChoices | null,
  text1?: string | null,
  page_size?: number,
  sort?: 'asc' | 'desc' | null
) => Promise<GetFormColumnsData>

export type GetInterfaceFormColumnsMethod = (
  interfaceId: string,
  componentId: string,
  paginateResults: boolean,
  page?: number,
  filter?: IndexTextFilterChoices | null,
  text1?: string | null,
  page_size?: number,
  sort?: 'asc' | 'desc' | null
) => Promise<GetFormColumnsData>

export interface IInputNodeToolbar {
  componentId: string
  interfaceOrNodeId: string
  allowAddRows: boolean
  initialRowsToAdd: number
  showConfirmCancelButtons: boolean
  hasUnconfirmedChanges: boolean
  eventCallback: (triggerData: InterfaceEvent) => void
  setHasUnconfirmedChanges: React.Dispatch<React.SetStateAction<boolean>>
  confirmChangesEvent: InterfaceEventType
  cancelChangesEvent: InterfaceEventType
  addRowEvent?: InterfaceEventType
  changeRowsToAddEvent?: InterfaceEventType
  refreshDataEvent?: InterfaceEventType
  context?: Context
  disableConfirmButton?: boolean
  showSelectColumnsButton?: boolean
  getFormColumnsMethod?: GetNodeFormColumnsMethod | GetInterfaceFormColumnsMethod
  formProperties?: FormProperties
  nodeId?: string
  showEditExcelButton?: boolean
  showRefreshButton?: boolean
  onOpenHistory?: () => void
}

export enum InputNodeHistorySource {
  interface = 'interface',
  diagram = 'diagram',
  htmlInterface = 'html_interface',
  agent = 'agent',
  officeAddin = 'office_addin',
  code = 'code'
}

export enum InputNodeHistoryOperation {
  update = 'update',
  insert = 'insert',
  delete = 'delete'
}

export type InputNodeHistoryValue =
  | string
  | number
  | boolean
  | null
  | InputNodeHistoryValue[]
  | { [key: string]: InputNodeHistoryValue }

export interface InputNodeHistoryItem {
  id: number
  row_id: string
  keys: Record<string, InputNodeHistoryValue> | null
  operation: InputNodeHistoryOperation
  column_name: string | null
  old_value: InputNodeHistoryValue
  new_value: InputNodeHistoryValue
  user_id: string | null
  user_name: string | null
  user_full_name: string | null
  source: InputNodeHistorySource
  app_uri: string | null
  app_version: string | null
  changed_at: string
}

export interface InputNodeHistoryPage {
  items: InputNodeHistoryItem[]
  total: number
  has_more: boolean
}

/**
 * Matches the records whose text columns contain text, or whose source, user or operation is in
 * one of the lists
 */
export interface InputNodeHistorySearch {
  text?: string
  sources?: InputNodeHistorySource[]
  user_ids?: string[]
  operations?: InputNodeHistoryOperation[]
}

export interface InputNodeHistoryFilters {
  user_ids?: string[]
  sources?: InputNodeHistorySource[]
  // ISO instants: the bounds of local date-times, converted by the browser
  date_from?: string
  date_to?: string
  search?: InputNodeHistorySearch
}

export interface InputNodeHistoryBatchesRequest extends InputNodeHistoryFilters {
  node_id: string
  limit?: number
  offset?: number
  // One item per edit session instead of per batch, for the batches list
  group_edit_sessions?: boolean
}

export interface InputNodeHistoryRequest extends InputNodeHistoryBatchesRequest {
  row_id?: string | number
  batch_ids?: string[]
  session_id?: string
}

export enum InputNodeHistoryExportColumn {
  changedAt = 'changed_at',
  userName = 'user_name',
  userFullName = 'user_full_name',
  source = 'source',
  operation = 'operation',
  rowId = 'row_id',
  columnName = 'column_name',
  oldValue = 'old_value',
  newValue = 'new_value',
  batchId = 'batch_id',
  appVersion = 'app_version'
}

/** Every change that matches, without pages, as a CSV that reads like the dialog */
export interface InputNodeHistoryExportRequest
  extends Omit<InputNodeHistoryRequest, 'limit' | 'offset'> {
  // The changes of the batches or users that a search of those lists matches
  batch_search?: InputNodeHistorySearch
  user_search?: InputNodeHistorySearch
  // IANA name of the browser's time zone
  time_zone: string
  separator: string
  decimal_separator: string
  headers: Partial<Record<InputNodeHistoryExportColumn, string>>
  source_labels: Partial<Record<InputNodeHistorySource, string>>
  operation_labels: Partial<Record<InputNodeHistoryOperation, string>>
  file_suffix: string
}

export interface InputNodeHistoryExport {
  data: Blob
  fileName: string | null
}

export interface InputNodeHistoryBatch {
  batch_id: string
  changed_at: string
  user_id: string | null
  user_name: string | null
  user_full_name: string | null
  source: InputNodeHistorySource
  app_version: string | null
  changed_cells: number
  changed_rows: number
  inserted_rows: number
  deleted_rows: number
  columns: Record<string, number>
  session_id: string
}

/**
 * An edit session: the newest of its batches, plus how many there are. A batch with more than one
 * change is a session of its own.
 */
export interface InputNodeHistorySession extends InputNodeHistoryBatch {
  batches: number
  first_changed_at: string
}

export interface InputNodeHistoryListPage<T> {
  items: T[]
  total: number
  has_more: boolean
}

export type InputNodeHistoryBatchesPage = InputNodeHistoryListPage<InputNodeHistorySession>

export interface InputNodeHistoryUserSummary {
  user_id: string
  user_name: string | null
  user_full_name: string | null
  batches: number
  changes: number
  last_changed_at: string
}

export interface InputNodeHistoryUser {
  user_id: string
  user_name: string | null
  user_full_name: string | null
}

export type GetInputNodeHistoryMethod = (
  request: InputNodeHistoryRequest
) => Promise<InputNodeHistoryPage>

export type GetInputNodeHistoryBatchesMethod = (
  request: InputNodeHistoryBatchesRequest
) => Promise<InputNodeHistoryBatchesPage>

export interface InputNodeHistoryMethods {
  getHistory: GetInputNodeHistoryMethod
  getBatches: GetInputNodeHistoryBatchesMethod
  getByUser: (
    request: InputNodeHistoryBatchesRequest
  ) => Promise<InputNodeHistoryListPage<InputNodeHistoryUserSummary>>
  getUsers: (nodeId: string) => Promise<InputNodeHistoryUser[]>
  exportHistory: (request: InputNodeHistoryExportRequest) => Promise<InputNodeHistoryExport>
}

export enum InputNodeVariant {
  FORM = 'form',
  INPUT_CUBE = 'inputCube'
}

export enum FormRowInsertMode {
  Beginning = 'beginning',
  End = 'end',
  Before = 'before',
  After = 'after'
}

// InputCube

interface InputCubeData extends TableData {
  has_unconfirmed_changes: boolean
  track_history?: boolean
}

export interface InputCubeResult extends TableResult {
  data: InputCubeData
  columns_details: ColumnsDetails
  filters: TableFilters
  has_write_permissions?: boolean
}

export interface InputCubeProperties extends TableProperties {
  confirm_on_change?: boolean
  invalidate_outputs_on_change?: boolean
  invalidate_outputs_on_confirm?: boolean
  update_calc_columns_on_change?: boolean
  show_history_icon?: boolean
}

export interface IInputCube extends InterfaceComponent {
  component_type: ComponentTypes.Core_InputCube
  properties: InputCubeProperties
  result: InputCubeResult
}

// Base Table

export interface BaseTableProps {
  component: ITable
  eventCallback: (triggerData: InterfaceEvent) => void
  editMode: boolean
  tableRef: RefObject<HotTableClass>
  context?: Context
  interfaceId?: string
  widgetResultDimensions?: { width: number; height: number }
  columns?: Handsontable.ColumnSettings[]
  additionalCellProperties?: (row: number, column: number) => Handsontable.CellMeta | undefined
  hotContextMenu?: Settings
  afterChange?:
    | ((changes: Handsontable.CellChange[] | null, source: Handsontable.ChangeSource) => void)
    | undefined
  afterCreateRow?:
    | ((index: number, amount: number, source?: Handsontable.ChangeSource | undefined) => void)
    | undefined
  beforeRemoveRow?:
    | ((
        index: number,
        amount: number,
        physicalColumns: number[],
        source?: Handsontable.ChangeSource | undefined
      ) => void)
    | undefined
  afterRemoveRow?:
    | ((
        index: number,
        amount: number,
        physicalRows: number[],
        source?: Handsontable.ChangeSource | undefined
      ) => void)
    | undefined
  extraToolbar?: JSX.Element
  afterPaste?: ((data: any[][], coords: RangeType[]) => void) | undefined
  showPivotToolbar?: boolean
  showQuickPivot?: boolean
  updateShowPivotToolbar?: () => void
  updateShowQuickPivot?: () => void
  nestedRows?: boolean
}

// DashApp

interface DashAppResult {
  link: string
}

export interface IDashApp extends InterfaceComponent {
  component_type: ComponentTypes.Core_DashApp
  properties: CommonProperties
  result: DashAppResult
}

export type PivoteableComponent = IChart | IIndicator | ITable

export type SortOrder = 'ascending' | 'descending' | 'custom'

export interface MultiSortRow {
  id: string
  dimension: string
  order: SortOrder
  customSortOrder: (string | number)[]
  indexNodeId: string | null
}

export interface MultiSortConfig {
  dimension: string
  order: SortOrder
  custom_sort_order: (string | number)[] | null
  index_node_id: string | null
}

// PlotlyChart

export interface IPlotlyChart extends InterfaceComponent {
  component_type: ComponentTypes.Core_PlotlyChart
  properties: ChartProperties
  result: ChartResult
}

// MatplotlibChart

export interface IMatplotlibChart extends InterfaceComponent {
  component_type: ComponentTypes.Core_MatplotlibChart
  properties: CommonProperties
  result: HTMLResult
}

// Assistant

export interface IAssistant extends InterfaceComponent {
  component_type: ComponentTypes.Core_Assistant
  properties: CommonProperties
  result: undefined
}
// My Task

export interface MyTasksProperties extends CommonProperties {
  columns_to_show: ColumnsToShow
  current_app_only?: boolean
  show_search?: boolean
  hide_disabled_tasks?: boolean
  sort_by?: string | null
  search_text?: string | null
  page?: number | null
}

export interface IMyTask extends InterfaceComponent {
  component_type: ComponentTypes.Core_MyTasks
  properties: MyTasksProperties
  result: undefined
}

export interface NotificationsProperties extends CommonProperties {
  current_app_only?: boolean
  allow_compose?: boolean
}

export interface INotifications extends InterfaceComponent {
  component_type: ComponentTypes.Core_Notifications
  properties: NotificationsProperties
  result: undefined
}

// Schedule tasks

/** Row actions the scheduled tasks widget can offer. */
export interface ActionsToShow {
  info?: boolean
  run_task?: boolean
  show_task_logs?: boolean
  [key: string]: boolean | undefined
}

export enum ScheduledTasksUpdateMode {
  Interval = 'interval',
  Live = 'live'
}

export interface ScheduledTasksProperties extends MyTasksProperties {
  actions_to_show?: ActionsToShow
  /** None/undefined is read as false. */
  automatically_update?: boolean | null
  automatic_update_mode?: ScheduledTasksUpdateMode | null
  /** Defaults to 60 seconds when not set. Only used in `interval` mode. */
  automatic_update_every_seconds?: number | null
  /** Values selected in each column filter, keyed by column name. */
  column_filters?: TaskColumnFilters | null
}

export interface IScheduledTasks extends InterfaceComponent {
  component_type: ComponentTypes.Core_ScheduledTasks
  properties: ScheduledTasksProperties
  result: undefined
}

// Export

/** Export declared by a table title in an HTML interface via `data-pyplan-export`. */
export interface HtmlExportInfo {
  /** @callback returning the table's current-view dataframe. Absent means the
   *  title opted in with an empty attribute: only full-node export is offered. */
  method?: string
  /** Current-view params captured from `data-pyplan-export-params` at click time. */
  params?: Record<string, unknown>
  /** A `pyplan.ui.table` exporting itself: `method` is its own callback and the
   *  dialog adds `request.export` - how to lay the file out - to `params`. */
  table?: boolean
}

/** Lightweight description of a DOM element picked with inspect mode in an
 *  HTML interface. Sent by the iframe shim (closest-first, element then
 *  ancestors) so the editor can locate the element in the HTML source when
 *  the element carries no `data-pp-line` — which is the case for any DOM
 *  built at runtime by the interface's own JavaScript. */
export interface InspectElementDescriptor {
  tag: string
  id: string
  classes: string[]
  /** Full textContent, descendants included. */
  text: string
  /** Text of the element's own text nodes only — far likelier to appear
   *  verbatim in the source than the concatenated textContent. */
  ownText: string
}

/** Payload of the `inspect-select` event emitted by the iframe shim. */
export interface InspectSelection {
  /** Server-annotated source line, when the picked element (or an ancestor)
   *  came from the static markup. */
  line: number | null
  /** Fallback trail used when `line` is null. */
  path: InspectElementDescriptor[]
}

export interface ExportComponentDialogProps {
  open: boolean
  setOpenExport: Dispatch<SetStateAction<boolean>>
  nodeId: string
  interfaceId: string
  componentInterfaceId?: string
  defaultFileName: string
  componentType?: string
  /** Present when exporting a table from an HTML interface instead of a
   *  drag&drop component. */
  htmlExport?: HtmlExportInfo
}

export enum ExportOptions {
  Full = 'Full',
  Current = 'Current'
}

/** How a table's file is laid out: as the table is drawn, or one row per figure. */
export enum TableExportShape {
  Seen = 'seen',
  List = 'list'
}

export interface TableExportOptions {
  shape: TableExportShape
  totals: boolean
  keepFormats: boolean
  /** A sheet to bring back to the table: its rows and columns marked, as seen. */
  edit?: boolean
}

/** The params a table's own callback is called with to export it: the request
 *  it was showing, with the `export` options added. */
export const tableParams = (
  params: Record<string, unknown> | undefined,
  options: TableExportOptions
): Record<string, unknown> => {
  const request =
    params?.request && typeof params.request === 'object'
      ? (params.request as Record<string, unknown>)
      : {}
  return { ...params, request: { ...request, export: options } }
}

export enum FileFormat {
  XLSX = 'xlsx',
  CSV = 'csv',
  XLS = 'xls'
}

export enum ColumnSeparator {
  Comma = ',',
  Tab = 'tab',
  Semicolon = ';'
}

export enum NumberFormat {
  TSCDSP = 'TSCDSP-123.45',
  TSPDSC = 'TSPDSC-123,45'
}

export enum FileType {
  CSV = 'text/csv',
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ZIP = 'application/zip'
}

export interface ExportData {
  columnFormat: string
  compressed: string
  fileFormat: string
  nodeId: string
  exclude: boolean
  numberFormat: string
  interfaceId: string
  exportType: string
  componentId?: string
  useTitles?: boolean
  includeNestedHeaders?: boolean
  customFileName?: string
}

export interface ExportHtmlViewData {
  columnFormat: string
  compressed: string
  fileFormat: string
  exclude: boolean
  numberFormat: string
  interfaceId: string
  exportType: string
  method?: string
  params?: Record<string, unknown>
  nodeId?: string
  customFileName?: string
}

export interface SelectorOptionsMax {
  componentId: string
  results: IndexesAndOptions
}

// UploadManager
export interface UploadValidationOptions {
  validate_sheets?: boolean
  validate_column_names?: boolean
  validate_column_types?: boolean
  validation_start_row?: number
}

export enum BasePathMode {
  relativeToApplication = 'relative_to_application',
  relativeToVersion = 'relative_to_version',
  custom = 'custom'
}

export enum UploadManagerAccessType {
  everyone = 'everyone',
  departments = 'departments',
  users = 'users'
}

export interface UploadManagerItemAccess {
  type: UploadManagerAccessType
  department_codes: string[]
  user_ids: string[]
}

export interface UploadManagerItem {
  name: string
  identifier?: string
  base_file_path?: string
  current_file_path: string
  current_folder_path?: string
  validation_node?: string
  callback_node?: string
  invalidate_node?: string
  run_nodes_on_restore?: boolean
  history_count?: number
  validation_options?: UploadValidationOptions
  preserve_existing_filename?: boolean
  use_folder_path?: boolean
  history_path?: string
  last_modified_date?: string
  last_modified_user?: string
  navigate_to_interface?: string
  navigate_to_interface_name?: string
  show_base_download?: boolean
  show_upload?: boolean
  show_download?: boolean
  show_history?: boolean
  base_path_mode?: BasePathMode
  info?: string
  file_size?: string
  access?: UploadManagerItemAccess | null
}

export interface UploadMAnagerResult {
  items: UploadManagerItem[]
  inaccessible_item_ids?: string[]
}

export interface UploadManagerProps extends CommonProperties {
  show_base_file?: boolean
  show_current_file: boolean
  show_last_modified_date: boolean
  show_last_modified_user: boolean
  show_file_size?: boolean
}

export interface IUploadManager extends InterfaceComponent {
  component_type: ComponentTypes.Core_UploadManager
  properties: UploadManagerProps
  result: UploadMAnagerResult
}

export interface FileUploadItemDialogProps {
  open: boolean
  initialItem?: UploadManagerItem
  onClose: () => void
  onSave: (item: UploadManagerItem) => void
}

export const VALIDATION_OPTION_KEYS = [
  'validate_sheets',
  'validate_column_names',
  'validate_column_types'
] as const

export interface UploadFileFlowResponse {
  status: 'success' | 'error'
  messages: UploadMessage[]
}

export interface DownloadFileOrFolderParams {
  params: {
    sources: string[]
  }
}

export interface DownloadFileOrFolderResponse {
  data: Blob | ArrayBuffer | string
}

export interface HistoryFileItem {
  file_name: string
  last_modification_date: string
  user: string
  complete_path: string
  file_size?: number | string
}

export interface ViewHistoryFilesProps {
  itemId: string
  componentId: string
  interfaceId: string
  onClose: () => void
  onDownload: (path: string, fileName?: string) => void
}
export type TableOrChartProperties = ChartProperties | TableProperties | FormProperties

export interface FolderFileItem {
  file_name: string
  file_size: number | string
  last_modified_date: string
  last_modified_user: string
  complete_path: string
}

export interface ViewFolderItemsFilesProps {
  item: UploadManagerItem
  onClose: () => void
  interfaceId: string
  componentId: string
  onDownload: (path: string, fileName?: string) => void
}

export interface UploadManagerMessages {
  key: string
  values?: string
}

export type UploadMessage = string | UploadManagerMessages
